import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export interface AuthResponse {
  accessToken: string;
  tokenType: string;
  expiresAt: string;
  profile: string;
  subject: string;
  displayName?: string | null;
}

export type SecondFactorDeliveryMethod = 'totp' | 'sms' | 'email' | 'push';

export interface SecondFactorRequiredResponse {
  requiresSecondFactor: true;
  challengeId: string;
  deliveryMethod: SecondFactorDeliveryMethod;
  maskedDestination?: string | null;
  expiresAt: string;
}

export interface SecondFactorEnrollmentRequiredResponse {
  requiresSecondFactorEnrollment: true;
  challengeId: string;
  method: 'totp';
  otpauthUri: string;
  manualEntryKey?: string | null;
  required: boolean;
  expiresAt: string;
}

export type AuthLoginResponse =
  | AuthResponse
  | SecondFactorRequiredResponse
  | SecondFactorEnrollmentRequiredResponse;

@Injectable({ providedIn: 'root' })
export class AuthService {
  async loginManager(username: string, password: string): Promise<Response> {
    return fetch(`${environment.apiAuthBaseUrl}/managers/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username,
        password
      })
    });
  }

  async loginGuard(username: string, password: string): Promise<Response> {
    return fetch(`${environment.apiAuthBaseUrl}/guards/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username,
        password
      })
    });
  }

  async loginIrrigatorByReceipt(ticket: string, receiptAmount: number): Promise<Response> {
    return fetch(`${environment.apiAuthBaseUrl}/irrigators/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        Ticket: ticket,
        ReceiptAmount: receiptAmount
      })
    });
  }

  async exchangeCertificateLoginCode(code: string): Promise<Response> {
    return fetch(`${environment.apiAuthBaseUrl}/certificate/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ Code: code })
    });
  }

  getCertificateLoginUrl(returnPath = '/auth/callback'): string {
    const returnUrl = new URL(returnPath, window.location.origin);
    const loginUrl = new URL(environment.certificateAuthLoginUrl);

    loginUrl.searchParams.set('returnUrl', returnUrl.toString());
    return loginUrl.toString();
  }

  persistAuthResponse(authResponse: AuthResponse): void {
    window.localStorage.setItem('ramAccessToken', authResponse.accessToken);
    window.localStorage.setItem('ramTokenType', authResponse.tokenType);
    window.localStorage.setItem('ramTokenExpiresAt', authResponse.expiresAt);
    window.localStorage.setItem('ramProfile', authResponse.profile);
    window.localStorage.setItem('ramSubject', authResponse.subject);

    if (authResponse.displayName) {
      window.localStorage.setItem('ramDisplayName', authResponse.displayName);
    } else {
      window.localStorage.removeItem('ramDisplayName');
    }
  }

  clearAuthResponse(): void {
    window.localStorage.removeItem('ramAccessToken');
    window.localStorage.removeItem('ramTokenType');
    window.localStorage.removeItem('ramTokenExpiresAt');
    window.localStorage.removeItem('ramProfile');
    window.localStorage.removeItem('ramSubject');
    window.localStorage.removeItem('ramDisplayName');
  }

  hasStoredAccessToken(): boolean {
    return Boolean(window.localStorage.getItem('ramAccessToken'));
  }

  getStoredProfile(): string | null {
    return window.localStorage.getItem('ramProfile');
  }

  isStoredTokenExpired(): boolean {
    const expiresAt = window.localStorage.getItem('ramTokenExpiresAt');

    if (!expiresAt) {
      return true;
    }

    const expiresAtTime = Date.parse(expiresAt);

    return !Number.isFinite(expiresAtTime) || expiresAtTime <= Date.now();
  }

  getAuthorizationHeaders(onExpired?: () => void): Record<string, string> {
    const accessToken = window.localStorage.getItem('ramAccessToken');
    const tokenType = window.localStorage.getItem('ramTokenType') || 'Bearer';

    if (accessToken && this.isStoredTokenExpired()) {
      this.clearAuthResponse();
      onExpired?.();
      return {};
    }

    return accessToken ? { Authorization: `${tokenType} ${accessToken}` } : {};
  }

  isAuthResponse(response: AuthLoginResponse | unknown): response is AuthResponse {
    return Boolean(
      response &&
      typeof response === 'object' &&
      'accessToken' in response &&
      'expiresAt' in response &&
      'profile' in response &&
      'subject' in response
    );
  }

  isSecondFactorRequiredResponse(response: AuthLoginResponse | unknown): response is SecondFactorRequiredResponse {
    return Boolean(
      response &&
      typeof response === 'object' &&
      'requiresSecondFactor' in response &&
      (response as SecondFactorRequiredResponse).requiresSecondFactor === true
    );
  }

  isSecondFactorEnrollmentRequiredResponse(response: AuthLoginResponse | unknown): response is SecondFactorEnrollmentRequiredResponse {
    return Boolean(
      response &&
      typeof response === 'object' &&
      'requiresSecondFactorEnrollment' in response &&
      (response as SecondFactorEnrollmentRequiredResponse).requiresSecondFactorEnrollment === true
    );
  }
}
